/**
 * @file VoidedDocumentDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-05
 *
 * What happens when someone tries to edit, restore or change the status of a
 * VOIDED invoice, credit note or bill (Cam, 5 October 2026). A voided
 * document cannot come back — not here, and not in Xero, where the only way is
 * "Copy to" a new document. So this says so, and offers two ways on:
 *
 *   - Raise a copy: the create screen, pre-filled with the same contact, lines
 *     and prices (a new number; a bill as a Draft). Nothing is written until
 *     that screen is saved. An invoice raised from an order is invoiced again
 *     from the order; a till invoice from Till Sales.
 *   - Email accounts: a pre-written message to accounts@excel-agri.com —
 *     which document, that it was voided and why it needs raising again, and
 *     the Xero steps — reviewed here and sent (tracked and audited) by
 *     POST /api/documents/voided/:kind/:id/email-accounts.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import { INVOICE_FROM_ORDER_KEY } from '../utils/invoiceFromOrder';
import { copyTarget } from '../utils/voidedDocument';
import './VoidedDocumentDialog.css';

function VoidedBody({ doc, step, email, setEmail, error, busy, copy }) {
  useSlideOutButton([step, busy, !!doc]);
  if (!doc) {
    return <div className="voided-body"><p className="voided-text">{error || 'Loading…'}</p></div>;
  }
  const Noun = doc.noun.charAt(0).toUpperCase() + doc.noun.slice(1);
  if (step === 'email') {
    return (
      <div className="voided-body">
        <div className="form-group">
          <label>To</label>
          <Input value={email.to} readOnly disabled />
        </div>
        <div className="form-group">
          <label>Subject</label>
          <Input value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.target.value })} disabled={busy} />
        </div>
        <div className="form-group">
          <label>Message</label>
          <Textarea rows={16} value={email.body} onChange={(e) => setEmail({ ...email, body: e.target.value })} disabled={busy} />
        </div>
        <p className="voided-hint">Say why it needs raising again on the line left for it, then send.</p>
        {error && (
          <div className="voided-note blocked" role="alert">
            <span className="material-symbols-rounded">error</span>
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="voided-body">
      <p className="voided-text">
        <strong>{Noun} {doc.number}</strong>{doc.contact ? ` (${doc.contact}` : ''}{doc.date ? `, ${formatDateShort(doc.date)}` : ''}
        {doc.contact ? `, ${formatCurrency(doc.total)})` : ` ${formatCurrency(doc.total)}`} is voided.
      </p>
      <p className="voided-text">
        A voided {doc.noun} cannot be edited or restored — not here, and not in Xero, where the only way back is
        to copy it to a new one. To put it right, raise a copy and approve that instead.
      </p>
      <div className="voided-note">
        <span className="material-symbols-rounded">info</span>
        <span>
          <strong>Raise a copy</strong> opens {copy.label} with {copy.what}.{' '}
          <strong>Email accounts</strong> asks accounts@excel-agri.com to do it in Xero (Copy to), with the steps.
        </span>
      </div>
      {copy.blocked && (
        <div className="voided-note blocked">
          <span className="material-symbols-rounded">block</span>
          <span>{copy.blocked}</span>
        </div>
      )}
      {error && (
        <div className="voided-note blocked" role="alert">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {'invoice'|'bill'} props.kind
 * @param {number} props.id
 * @param {function} props.onClose
 * @param {function} [props.onNavigate] — (menu) to open the create screen
 * @param {string} [props.returnTo] — where a bill copy returns on save
 * @param {function} [props.onSent] — (message) after the email went
 */
function VoidedDocumentDialog({ kind, id, onClose, onNavigate, returnTo = null, onSent }) {
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [step, setStep] = useState('explain');
  const [email, setEmail] = useState({ to: '', subject: '', body: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/documents/voided/${kind}/${id}`, { credentials: 'include' })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok || !d.success) { setError(d.error || 'This document could not be loaded.'); return; }
        setDoc(d.document);
        setDraft(d.draft);
      })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [kind, id]);

  const copy = copyTarget(kind, doc);

  const raiseCopy = () => {
    if (!doc || copy.blocked || !onNavigate) return;
    if (copy.storageKey === INVOICE_FROM_ORDER_KEY) {
      sessionStorage.setItem(INVOICE_FROM_ORDER_KEY, String(doc.customerOrderId));
    } else {
      sessionStorage.setItem(copy.storageKey, String(doc.id));
      if (kind === 'bill' && returnTo) sessionStorage.setItem('createBill.returnTo', returnTo);
    }
    onClose && onClose();
    onNavigate(copy.menu);
  };

  const openEmail = () => {
    if (!draft) return;
    setEmail({ to: draft.to, subject: draft.subject, body: draft.body });
    setError(null);
    setStep('email');
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/documents/voided/${kind}/${id}/email-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subject: email.subject, body: email.body })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) { setError(data.error || 'The email could not be sent.'); return; }
      onSent && onSent(`Sent to ${data.to}.`);
      onClose && onClose();
    } catch (err) {
      setError(`The email could not be sent: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const Noun = doc ? doc.noun.charAt(0).toUpperCase() + doc.noun.slice(1) : 'Document';
  const footer = step === 'email' ? (
    <div className="buttons slideout">
      <div>
        <button className="btn-slideout" onClick={() => { setStep('explain'); setError(null); }} disabled={busy}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_back</span></div>
          <span className="btn-label">Back</span>
        </button>
      </div>
      <div>
        <button className="btn-slideout" onClick={send} disabled={busy || !email.subject.trim() || !email.body.trim()}>
          <div className="icon-wrapper">
            <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>{busy ? 'progress_activity' : 'send'}</span>
          </div>
          <span className="btn-label">{busy ? 'Sending…' : 'Send to accounts'}</span>
        </button>
      </div>
    </div>
  ) : (
    <div className="buttons slideout">
      <div>
        <button className="btn-slideout" onClick={onClose} data-read-safe>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Close</span>
        </button>
      </div>
      <div>
        <button className="btn-slideout" onClick={openEmail} disabled={!draft} title="Ask accounts@excel-agri.com to copy it in Xero">
          <div className="icon-wrapper"><span className="material-symbols-rounded">mail</span></div>
          <span className="btn-label">Email accounts</span>
        </button>
      </div>
      <div>
        <button
          className="btn-slideout"
          onClick={raiseCopy}
          disabled={!doc || !!copy.blocked || !onNavigate}
          title={copy.blocked || (onNavigate ? `Open ${copy.label}, filled in` : 'Open this from its search screen to raise a copy')}
        >
          <div className="icon-wrapper"><span className="material-symbols-rounded">content_copy</span></div>
          <span className="btn-label">Raise a copy</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : onClose}
      title={step === 'email' ? 'Email accounts' : `${Noun} is voided`}
      size={step === 'email' ? 'lg' : 'md'}
      className="voided-document"
      footer={footer}
    >
      <VoidedBody doc={doc} step={step} email={email} setEmail={setEmail} error={error} busy={busy} copy={copy} />
    </Modal>
  );
}

export default VoidedDocumentDialog;
